import { useContext, useEffect, useState } from "react";
import {
  Box,
  Chip,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { AttachFile, Article, Close, ArrowBack, FolderOff, Visibility } from "@mui/icons-material";
import { map as fpMap } from "lodash/fp";
import { map } from "lodash";
import { useMutation } from "@apollo/client/react";
import { KickOffContext } from "@/Contexts/KickOff";
import { AttachKickOffDocumentsDocument } from "@/graphql/generated/graphql";
import useFileUploader from "@/hooks/useFileUploader";
import { objectLength } from "@/utils/helperFunctions";
import { ACCEPTED_ATTACHMENT_FORMATS, MAX_ATTACHMENTS_ALLOWED } from "@/utils/constants";
import { VisuallyHiddenInput } from "@/theme/styled-components";
import MsDocumentViewer from "@/components/DocumentsViewer/MsDocumentsViewer";
import { openKickOffDocumentDrawer } from "@/graphql/reactiveVars";

const mapSignedBlobId = fpMap("signedBlobId");

interface Attachment {
  filename: string
  previewUrl: string
}

const KickOffDocumentDrawer: React.FC = () => {
  const [selectedAttachment, setSelectedAttachment] = useState<Attachment | null>(null)
  const { isUploading, files, handleUploadImage } = useFileUploader()
  const { kickoffData, projectData } = useContext(KickOffContext)
  const [attachDocuments] = useMutation(AttachKickOffDocumentsDocument, {
    refetchQueries: ["getKickOff"],
  })

  useEffect(() => {
    if (objectLength(files) > 0) {
      attachDocuments({
        variables: {
          id: kickoffData?.id as string,
          signedIds: mapSignedBlobId(files),
        },
      })
    }
  }, [files]) // eslint-disable-line react-hooks/exhaustive-deps

  const kickoffAttachments: Attachment[] = kickoffData?.attachments || []
  const projectAttachments: Attachment[] = projectData?.attachments || []
  const canAttachMore = objectLength(files) < MAX_ATTACHMENTS_ALLOWED && !isUploading

  return (
    <Grid
      size={6}
      sx={{
        borderLeft: "1px solid",
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        pl: 2,
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Documents
          {(objectLength(kickoffAttachments) + objectLength(projectAttachments)) > 0 && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              ({objectLength(kickoffAttachments) + objectLength(projectAttachments)})
            </Typography>
          )}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isUploading && <Chip label="Uploading…" size="small" color="info" variant="outlined" />}
          <Tooltip title={canAttachMore ? "Attach file" : isUploading ? "Uploading…" : "Maximum attachments reached"}>
            <span>
              <IconButton component="label" size="small" disabled={!canAttachMore}>
                <AttachFile fontSize="small" />
                <VisuallyHiddenInput
                  type="file"
                  accept={ACCEPTED_ATTACHMENT_FORMATS}
                  onChange={(e) => handleUploadImage(e, "kick-offs")}
                  disabled={!canAttachMore}
                />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Close panel">
            <IconButton size="small" onClick={() => openKickOffDocumentDrawer(false)}>
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Divider sx={{ mb: 1 }} />

      {/* Document viewer */}
      {selectedAttachment ? (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, cursor: "pointer", width: "fit-content" }}
            onClick={() => setSelectedAttachment(null)}
          >
            <ArrowBack fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 280 }}>
              {selectedAttachment.filename}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <MsDocumentViewer url={selectedAttachment.previewUrl} />
          </Box>
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflowY: "auto" }}>
          <DocumentSection
            title="Kick-Off Attachments"
            attachments={kickoffAttachments}
            onSelect={setSelectedAttachment}
          />
          {objectLength(projectAttachments) > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <DocumentSection
                title="Project Documents"
                attachments={projectAttachments}
                onSelect={setSelectedAttachment}
              />
            </>
          )}
          {objectLength(kickoffAttachments) === 0 && objectLength(projectAttachments) === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 6, color: "text.disabled", gap: 1 }}>
              <FolderOff sx={{ fontSize: 40 }} />
              <Typography variant="body2">No documents attached yet</Typography>
              <Typography variant="caption">Use the attach button above to add files</Typography>
            </Box>
          )}
        </Box>
      )}
    </Grid>
  )
}

interface DocumentSectionProps {
  title: string
  attachments: Attachment[]
  onSelect: (a: Attachment) => void
}

const DocumentSection: React.FC<DocumentSectionProps> = ({ title, attachments, onSelect }) => (
  <>
    <Typography variant="caption" color="text.secondary" sx={{ px: 1, textTransform: "uppercase", letterSpacing: 0.5 }}>
      {title} ({objectLength(attachments)})
    </Typography>
    {objectLength(attachments) === 0 ? (
      <Typography variant="body2" color="text.disabled" sx={{ px: 1, py: 0.5 }}>
        None
      </Typography>
    ) : (
      <List dense disablePadding>
        {map(attachments, (doc) => (
          <ListItemButton key={doc.previewUrl} onClick={() => onSelect(doc)} sx={{ borderRadius: 1 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Article fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={doc.filename}
              slotProps={{ primary: { variant: "body2", noWrap: true } }}
            />
            <Visibility fontSize="small" sx={{ color: "text.disabled", flexShrink: 0 }} />
          </ListItemButton>
        ))}
      </List>
    )}
  </>
)

export default KickOffDocumentDrawer;
